import React, { useEffect, useState } from 'react';
import { useWorkflowState, useWorkflowActions } from '../../stores/useWorkflowStore';
import { WORKFLOW_TYPES, SCAN_PROCESS_STEPS, MAIN_PROCESS_STEPS } from '../../constants/flowConfig';
import { useShallow } from 'zustand/shallow';
import styles from './WorkflowStepper.module.css';

const WorkflowStepper = () => {
  const [currentWorkflow, currentStep, stepCompletion, patientData] = useWorkflowState(
    useShallow((state) => [
      state.currentWorkflow,
      state.currentStep,
      state.stepCompletion,
      state.patientData
    ])
  );
  const { setCurrentStep, setCurrentWorkflow, canNavigateToStep } = useWorkflowActions();

  // Define workflow phases for the dashboard
  const workflowPhases = [
    {
      id: 'FETCH_DETAILS',
      label: 'Fetch Patient Details',
      description: 'Get patient information and measurements',
      completed: !!patientData
    },
    {
      id: 'SCAN_PROCESS',
      label: 'Scan Process',
      description: 'Prepare and align the 3D scan',
      completed: stepCompletion[SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW],
      subSteps: [
        { id: SCAN_PROCESS_STEPS.PREPARING_SCAN, label: 'Preparing Scan' },
        { id: SCAN_PROCESS_STEPS.AXES_ALIGNMENT, label: 'Axes & Alignment' },
        { id: SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW, label: 'Final Preview' }
      ]
    },
    {
      id: 'MAIN_PROCESS',
      label: 'Main Process',
      description: 'Design the prosthetic socket',
      completed: stepCompletion[MAIN_PROCESS_STEPS.FINALIZATION],
      subSteps: [
        { id: MAIN_PROCESS_STEPS.RECTIFICATION, label: 'Rectification' },
        { id: MAIN_PROCESS_STEPS.ELONGATION, label: 'Elongation' },
        { id: MAIN_PROCESS_STEPS.TRIMLINE, label: 'Trimline' },
        { id: MAIN_PROCESS_STEPS.FLARES, label: 'Flares' },
        { id: MAIN_PROCESS_STEPS.THICKNESS, label: 'Thickness' },
        { id: MAIN_PROCESS_STEPS.LOCKING_MECHANISM, label: 'Locking Mechanism' },
        { id: MAIN_PROCESS_STEPS.ALIGNMENT, label: 'Alignment' },
        { id: MAIN_PROCESS_STEPS.VALVE, label: 'Valve' },
        { id: MAIN_PROCESS_STEPS.PADS, label: 'Pads' },
        { id: MAIN_PROCESS_STEPS.FINALIZATION, label: 'Finalization' }
      ]
    }
  ];

  const handlePhaseClick = (phase) => {
    if (phase.id === 'FETCH_DETAILS') {
      // Can't navigate to fetch details if already completed
      return;
    }
    
    if (phase.id === 'SCAN_PROCESS' && canNavigateToStep(SCAN_PROCESS_STEPS.PREPARING_SCAN)) {
      setCurrentWorkflow(WORKFLOW_TYPES.SCAN_PROCESS);
      setCurrentStep(SCAN_PROCESS_STEPS.PREPARING_SCAN);
    } else if (phase.id === 'MAIN_PROCESS' && canNavigateToStep(MAIN_PROCESS_STEPS.RECTIFICATION)) {
      setCurrentWorkflow(WORKFLOW_TYPES.MAIN_PROCESS);
      setCurrentStep(MAIN_PROCESS_STEPS.RECTIFICATION);
    }
  };

  const getCurrentPhaseIndex = () => {
    if (!patientData) return 0;
    if (currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS) return 1;
    if (currentWorkflow === WORKFLOW_TYPES.MAIN_PROCESS) return 2;
    return 0;
  };

  const currentPhaseIndex = getCurrentPhaseIndex();

  return (
    <div className={styles.card}>
      <h2 className={styles.title}>Prosthetic Design Workflow</h2>
      <p className={styles.subtitle}>Complete each phase to design the prosthetic socket</p>
      
      <div className='flex w-full gap-28'>
        <div className='flex flex-col w-full gap-1 items-center justify-between select-none lg:flex-row'>
        {workflowPhases.map((phase, index) => (
          <React.Fragment key={phase.id}>
            <div
              className={`
                ${styles.step}
                ${index === currentPhaseIndex ? styles.active : ''}
                ${phase.completed ? styles.done : ''}
                ${!patientData && index > 0 ? styles.disabled : ''}
              `}
              onClick={() => handlePhaseClick(phase)}
            >
              <div className={styles.icon}>
                {phase.completed ? <span>&#10003;</span> : index + 1}
              </div>
              <div className={styles.label}>{phase.label}</div>
              <div className={styles.description}>{phase.description}</div>
            </div>

            {index < workflowPhases.length - 1 && (
              <div className={`${styles.connector} ${phase.completed ? styles.done : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Show current progress */}
      {patientData && (
        <div className='flex flex-col justify-evenly gap-4 bg-[#f6f6f6] rounded-2xl border border-gray-300 py-2 px-5'>
          <h3 className='text-center text-lg font-semibold'>Current Progress</h3>
          <div className='flex flex-col gap-4 justify-evenly align-middle'>
            <div className='flex flex-row gap-4 justify-evenly align-middle'>
              <span className={styles.statLabel}>Scan Process:</span>
              <span className={styles.statValue}>
                {workflowPhases[1].subSteps.filter(step => stepCompletion[step.id]).length} / {workflowPhases[1].subSteps.length} steps
              </span>
            </div>
            <div className='flex flex-row gap-4 justify-evenly align-middle'>
              <span className={styles.statLabel}>Main Process:</span>
              <span className={styles.statValue}>
                {workflowPhases[2].subSteps.filter(step => stepCompletion[step.id]).length} / {workflowPhases[2].subSteps.length} steps
              </span>
            </div>
          </div>
        </div>
      )}  
      </div>
    </div>
  );
};

export default WorkflowStepper;